import { useEffect, useRef, useState } from "react";
import type { Canon, Category, DifficultyFilter } from "@shared/types";
import {
  ALL_CATEGORIES,
  CATEGORY_LABELS,
  DIFFICULTY_FILTER_LABELS,
  GAME,
} from "@shared/config";
import { questionCount, recordModePick, type SoloConfig } from "../lib/api";
import { getActiveProfile, type LocalProfile } from "../lib/profile";
import { getRacesFinished } from "../lib/stats";
import SoundToggle from "../components/SoundToggle";
import ProfileChip from "../components/ProfileChip";
import ProfileChooser from "../components/ProfileChooser";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";
import RaceCar from "../components/brand/RaceCar";

const DIFFICULTIES: DifficultyFilter[] = ["mixed", "easy", "medium", "hard"];
const CANONS: { value: Canon; label: string }[] = [
  { value: "protestant-66", label: "Protestant (66)" },
  { value: "catholic-73", label: "Catholic (73)" },
  { value: "orthodox", label: "Orthodox" },
];

export default function Home({
  onStart,
  onQuickRace,
  onRaceFriends,
  onDaily,
  onLeaderboard,
  onViewProfile,
}: {
  onStart: (c: SoloConfig) => void;
  onQuickRace: () => void;
  onRaceFriends: () => void;
  onDaily: () => void;
  onLeaderboard: () => void;
  onViewProfile: (profile: LocalProfile) => void;
}) {
  const [difficulty, setDifficulty] = useState<DifficultyFilter>("mixed");
  const [categories, setCategories] = useState<Category[]>([]);
  const [canon, setCanon] = useState<Canon>("protestant-66");
  const [finishLine, setFinishLine] = useState<number>(GAME.defaultFinishLine);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [activeProfile, setActiveProfile] = useState<LocalProfile | null>(() =>
    getActiveProfile(),
  );
  const [chooserOpen, setChooserOpen] = useState(false);
  const racesFinished = getRacesFinished();
  const optionsRef = useRef<HTMLDivElement>(null);

  function toggleCategory(c: Category) {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }

  // Live count of questions matching the current filters, so the race length is
  // visible before starting (debounced; ignores stale responses).
  useEffect(() => {
    let active = true;
    setMatchCount(null);
    const t = window.setTimeout(() => {
      questionCount({ categories, difficulty, canon })
        .then((r) => {
          if (active) setMatchCount(r.count);
        })
        .catch(() => {
          if (active) setMatchCount(null);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(t);
    };
  }, [categories, difficulty, canon]);

  const raceLength = matchCount === null ? null : Math.min(finishLine, matchCount);
  const noMatches = matchCount === 0;

  function start() {
    if (noMatches) return;
    recordModePick("solo");
    onStart({ categories, difficulty, canon, finishLine });
  }

  /** Tally the mode press (aggregate, de-identified) on the way out of Home, so
   *  the admin funnel can compare "picked it" against "actually started it". */
  const pick =
    (mode: Parameters<typeof recordModePick>[0], go: () => void) => () => {
      recordModePick(mode);
      go();
    };

  return (
    <div className="relative min-h-full overflow-x-hidden">
      <SceneBackground />

      <main className="mx-auto w-full max-w-4xl px-4 pb-16 pt-4 sm:pt-6">
        {/* top bar: racer chip + progress coin + sound/settings */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <ProfileChip profile={activeProfile} onClick={() => setChooserOpen(true)} />
            <div className="coin-badge hidden text-lg sm:flex sm:text-xl">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full border-[2px] border-coin-edge text-sm text-ink"
                style={{
                  background:
                    "radial-gradient(circle at 38% 34%, #fff3b0, #ffd23f 60%, #f2a900)",
                }}
                aria-hidden
              >
                ✝
              </span>
              <span className="tabular-nums">{racesFinished}</span>
              <span className="text-sm font-semibold text-ink/60">races</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="icon-btn"
              aria-label="Race options"
              title="Race options"
              onClick={() =>
                optionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            >
              <span aria-hidden>⚙️</span>
            </button>
            <SoundToggle />
          </div>
        </div>

        {/* hero lockup */}
        <div className="flex flex-col items-center text-center">
          <div className="mt-2 sm:mt-4">
            <Wordmark width={720} animate />
          </div>

          {/* road + car */}
          <div className="relative mt-7 w-full max-w-2xl">
            {/* road band */}
            <div className="absolute inset-x-0 bottom-0 h-[120px] overflow-hidden rounded-3xl border-[5px] border-road-edge bg-road-surface">
              {/* checkered curb */}
              <div
                className="absolute inset-x-0 top-0 h-3"
                style={{
                  background:
                    "repeating-conic-gradient(#1c2a63 0% 25%, #fff 0% 50%) 0 0/24px 24px",
                }}
              />
              {/* dashed centre line */}
              <div
                className="animate-road absolute inset-x-0 top-1/2 h-2.5 -translate-y-1/2 rounded"
                style={{
                  background:
                    "repeating-linear-gradient(90deg, #ffd23f 0 56px, transparent 56px 112px)",
                }}
              />
            </div>
            {/* car sits on the road */}
            <div className="relative flex justify-center pb-3">
              <RaceCar
                width={460}
                number={1}
                spinning
                bobbing
                ariaLabel="BibleRacers race car"
                className="w-[min(460px,86%)]"
              />
            </div>
          </div>

          {/* PLAY */}
          <button
            type="button"
            className="btn-play animate-play mt-9"
            onClick={start}
            disabled={noMatches}
          >
            <span aria-hidden>▶</span> PLAY
            <span className="sheen animate-shine" aria-hidden />
          </button>

          {/* Multiplayer entries */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
            <button
              type="button"
              className="btn-chunk bg-grass px-6 py-3 text-lg text-white"
              onClick={pick("quick", onQuickRace)}
            >
              <span aria-hidden>🏎️</span> Quick Race
            </button>
            <button
              type="button"
              className="btn-chunk bg-grape px-6 py-3 text-lg text-white"
              onClick={pick("room", onRaceFriends)}
            >
              <span aria-hidden>👥</span> Race Friends
            </button>
          </div>

          {/* Daily challenge + leaderboards */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5">
            <button
              type="button"
              className="btn-chunk bg-sunny px-5 py-2.5 text-ink"
              onClick={pick("daily", onDaily)}
            >
              <span aria-hidden>📅</span> Daily Challenge
            </button>
            <button
              type="button"
              className="btn-chunk bg-white px-5 py-2.5 text-ink"
              onClick={pick("leaderboard", onLeaderboard)}
            >
              <span aria-hidden>🏆</span> Leaderboards
            </button>
          </div>

          {/* live race length */}
          <div className="mt-4 min-h-[1.5rem] text-sm font-semibold">
            {matchCount === null ? (
              <span className="text-ink/40">Warming up the engines…</span>
            ) : noMatches ? (
              <span className="text-flame">
                No questions match — pick more categories below.
              </span>
            ) : (
              <span className="text-ink/70">
                🏁 <span className="font-bold text-ink">{matchCount}</span> question
                {matchCount === 1 ? "" : "s"} ready · race to{" "}
                <span className="font-bold text-ink">{raceLength}</span> correct
              </span>
            )}
          </div>
        </div>

        {/* race options */}
        <section ref={optionsRef} className="panel mt-10 scroll-mt-4 p-5 sm:p-7">
          <h2 className="font-display text-2xl text-ink sm:text-3xl">Pick your race</h2>
          <p className="mt-1 text-sm text-ink/60">
            Choose how tricky and what to quiz on — or just smash PLAY!
          </p>

          <div className="mt-5">
            <div className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/70">
              How tricky?
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDifficulty(d)}
                  aria-pressed={difficulty === d}
                  className={`btn-chunk text-sm ${
                    difficulty === d ? "bg-flame text-white" : "bg-white"
                  }`}
                >
                  {DIFFICULTY_FILTER_LABELS[d]}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-bold uppercase tracking-wide text-ink/70">
                What to quiz on?
              </span>
              <span className="text-xs font-semibold text-ink/45">
                {categories.length === 0
                  ? "Everything"
                  : `${categories.length} picked`}
              </span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {ALL_CATEGORIES.map((c) => {
                const active = categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    aria-pressed={active}
                    className={`chip-chunk ${active ? "bg-grass text-white" : "bg-white"}`}
                  >
                    {CATEGORY_LABELS[c]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* advanced */}
          <div className="mt-6 border-t-[3px] border-dashed border-ink/15 pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="flex w-full items-center justify-between text-sm font-bold text-ink/70"
              aria-expanded={showAdvanced}
            >
              <span>Grown-up options</span>
              <span aria-hidden>{showAdvanced ? "▲" : "▼"}</span>
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-5">
                <div>
                  <div className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/70">
                    Canon
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    {CANONS.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setCanon(c.value)}
                        aria-pressed={canon === c.value}
                        className={`btn-chunk text-xs sm:text-sm ${
                          canon === c.value ? "bg-grape text-white" : "bg-white"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-ink/50">
                    Protestant (66) hides Apocrypha-dependent questions. Wider canons
                    include them.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="finishLine"
                    className="mb-2 flex items-center justify-between text-sm font-bold uppercase tracking-wide text-ink/70"
                  >
                    <span>Finish line</span>
                    <span className="tabular-nums text-flame">{finishLine} correct</span>
                  </label>
                  <input
                    id="finishLine"
                    type="range"
                    min={GAME.minFinishLine}
                    max={GAME.maxFinishLine}
                    step={1}
                    value={finishLine}
                    onChange={(e) => setFinishLine(Number(e.target.value))}
                    className="w-full accent-flame"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            disabled={noMatches}
            className="btn-play mt-7 w-full"
            onClick={start}
          >
            <span aria-hidden>▶</span> START RACE
            <span className="sheen animate-shine" aria-hidden />
          </button>

          {matchCount !== null &&
            raceLength !== null &&
            raceLength < finishLine &&
            !noMatches && (
              <p className="mt-3 text-center text-xs font-semibold text-ink/55">
                This filter is short on questions, so the race is {raceLength} long.
                Pick more categories or “Mixed” for a longer race.
              </p>
            )}
        </section>

        <p className="mt-6 text-center text-xs font-medium text-ink/50">
          Questions from the public-domain World English Bible (WEB). Every answer
          shows its scripture reference. No sign-up, just race! 🏁
        </p>
        <p className="mt-2 text-center text-xs font-medium text-ink/50">
          Made for kids &amp; families — no email, no ads, no tracking.{" "}
          <a href="/privacy" className="font-bold text-grape underline">
            Privacy Policy
          </a>
        </p>
      </main>

      {chooserOpen && (
        <ProfileChooser
          onClose={() => setChooserOpen(false)}
          onChange={(p) => setActiveProfile(p)}
          onViewProfile={onViewProfile}
        />
      )}
    </div>
  );
}
